import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { moduleService } from '../../services'
import { useAuth } from '../../auth/auth.context'
import { useLocale } from '../../hooks/use-locale'
import PageLayout from '../../components/layout/page-layout'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import Table from '../../components/ui/table'
import { Button } from '../../components/ui/button'
import { buttonVariants } from '../../components/ui/button-variants'
import { ButtonBig } from '../../components/buttons/button-big'
import { Container } from '../../components/ui/container'
import { PageHeader } from '../../components/layout/page-header'
import { IconActionButton } from '../../components/buttons/icon-action-button'
import { BookPlus } from 'lucide-react'
import { Badge } from '../../components/ui/badge'

export default function WeiterbildungList() {
  const { t } = useTranslation()
  const { formatDate } = useLocale()
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['weiterbildung-modules'],
    queryFn: () => moduleService.getModules({ type: 'weiterbildung', limit: 100 }),
  })
  const { user, company } = useAuth()
  const [deletingWeiterbildungenId, setDeletingWeiterbildungenId] = useState<string | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)

  const { data: allModulesData } = useQuery({
    queryKey: ['modules'],
    queryFn: () => moduleService.getModules(),
  })

  const limits = (company as any)?.limits as { maxLicenses: number } | undefined
  const totalModuleCount = allModulesData?.modules?.length || 0
  const licenseLimitReached = !!(limits && totalModuleCount >= limits.maxLicenses)

  const deleteWeiterbildungenMutation = useMutation({
    mutationFn: (id: string) => moduleService.deleteModule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weiterbildung-modules'] })
      setShowDeleteConfirm(null)
      setDeletingWeiterbildungenId(null)
    },
    onError: () => {
      setDeletingWeiterbildungenId(null)
    },
  })

  const handleDelete = (moduleId: string) => {
    setDeletingWeiterbildungenId(moduleId)
    deleteWeiterbildungenMutation.mutate(moduleId)
  }

  const modules = (data?.modules || []).filter(
    module => module.type === 'weiterbildung' || module.type == null
  )

  return (
    <PageLayout
      headerProps={{
        title: t('weiterbildung.title'),
        subtitle: t('weiterbildung.subtitle'),
      }}
      isLoading={isLoading}
      error={error}
      loadingMessage={t('weiterbildung.loading')}
      errorMessage={t('errors.loadModules', { type: t('common.typeWeiterbildung') })}
    >
      <Container>
        <PageHeader title={t('weiterbildung.title')} subtitle={t('weiterbildung.subtitle')} />

        <div className='grid grid-cols-1 sm:grid-cols-4 gap-4'>
          <ButtonBig title={t('weiterbildung.title')} value={modules.length} />
          <ButtonBig
            title={`${t('common.active')} ${t('weiterbildung.title')}`}
            value={modules.filter(m => m.isActive).length}
          />
          <ButtonBig
            title={t('common.inProgress')}
            value={modules.filter(m => !m.isActive).length}
          />
          <IconActionButton
            icon={BookPlus}
            label={t('weiterbildung.newWeiterbildung')}
            to={licenseLimitReached ? undefined : '/weiterbildung/new'}
            disabled={licenseLimitReached}
            hint={licenseLimitReached ? t('common.limitReached') : undefined}
          />
        </div>

        <Table
          dense
          data={modules}
          rowKey={m => m.id}
          emptyMessage={t('weiterbildung.empty')}
          columns={[
            {
              header: t('common.title'),
              cell: m => (
                <Link to={`/weiterbildung/${m.id}`} className='flex items-center gap-3 group'>
                  <div className={cnBadge(m.isActive)} />
                  <div className='min-w-0'>
                    <div className='font-medium text-neutral-900 group-hover:text-primary-600 transition-colors'>
                      {m.title}
                    </div>
                    {m.description && (
                      <div className='text-xs text-gray-500 line-clamp-1'>{m.description}</div>
                    )}
                  </div>
                </Link>
              ),
            },
            {
              header: t('common.status'),
              cell: m => (
                <Badge
                  title={m.isActive ? t('common.active') : t('common.inactive')}
                  variant={m.isActive ? 'success' : 'error'}
                />
              ),
              className: 'w-32',
            },
            {
              header: t('common.created'),
              cell: m => (
                <span className='text-sm text-gray-600'>
                  {m.createdAt ? formatDate(m.createdAt) : t('common.dash')}
                </span>
              ),
              className: 'w-40',
            },
            {
              header: t('common.actions'),
              cell: m => (
                <div className='flex gap-2 justify-end'>
                  {user?.role === 'ADMIN' && (
                    <Button
                      variant='secondary'
                      size='sm'
                      onClick={() => setShowDeleteConfirm(m.id)}
                      loading={deletingWeiterbildungenId === m.id}
                    >
                      {t('common.delete')}
                    </Button>
                  )}
                  <Link
                    to={`/weiterbildung/${m.id}`}
                    className={buttonVariants({ variant: 'outline', size: 'sm' })}
                  >
                    {t('common.open')}
                  </Link>
                </div>
              ),
              className: 'w-56',
            },
          ]}
        />

        {modules.length === 0 && user?.role === 'ADMIN' && (
          <div className='text-center'>
            <Link
              to='/weiterbildung/new'
              className={buttonVariants({ variant: 'primary', size: 'md' })}
            >
              {t('common.createNewType', { type: t('common.typeWeiterbildung') })}
            </Link>
          </div>
        )}
      </Container>

      {showDeleteConfirm && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>
              {t('weiterbildung.deleteTitle')}
            </h3>
            <p className='text-gray-600 mb-6'>{t('weiterbildung.deleteMessage')}</p>
            <div className='flex justify-end gap-3'>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className='px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors'
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={deletingWeiterbildungenId === showDeleteConfirm}
                className='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors'
              >
                {deletingWeiterbildungenId === showDeleteConfirm
                  ? t('common.deleting')
                  : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}

function cnBadge(isActive: boolean) {
  return `w-2.5 h-2.5 rounded-full ${isActive ? 'bg-green-500' : 'bg-yellow-500'}`
}
